// src/polaris-components/OuterLayout.tsx
import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import Sidebar from "./Sidebar";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import { PaymentFailureBanner } from "./PaymentFailureBanner";
import { SubscriptionSuspendedModal } from "./SubscriptionSuspendedModal";
import {
    toAbsoluteUrl,
    useMyAvatar,
} from "../utils/profilePictureUtils";
import { getTermsAccepted, getHasProfile } from "../utils/auth";
import { usePaymentStatus } from "../hooks/usePaymentStatus";
import { useAuth } from "../contexts/AuthContext";
import { IncompleteAdminProfileProvider } from "../contexts/IncompleteAdminProfileContext";
import TeamChatOverlay from "./team-chat/TeamChatOverlay";

import { API_BASE_URL } from "../utils/apiConfig";

import "../styles/layout.css";
import "../styles/staff-page-layout.css";
import { apiClient } from "../utils/apiClient";

/* ---------------- helpers ---------------- */

/** Get userId from localStorage (set during login/OAuth) */
function getUserIdFromStorage(): string | null {
    const userId = localStorage.getItem("loggedInUserId") || localStorage.getItem("userId");
    if (userId && userId.trim()) return userId.trim();
    return null;
}

/* ---------------- component ---------------- */

export default function OuterLayout() {
    const location = useLocation();
    const { isAuthenticated, userInfo } = useAuth();

    // Company logo for rail sidebar header (presigned URL from profile — no logo blob fetch)
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    /** Bumps when logo refreshes so the rail <img> remounts after upload/replace. */
    const [companyLogoVersion, setCompanyLogoVersion] = useState(0);
    const [currentViewedUserId, setCurrentViewedUserId] = useState<string | null>(null);

    // Terms and conditions modal state
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

    // Payment status - check for past due or suspended subscription
    const {
        shouldShowBanner: isPastDueBanner,
        shouldBlockAccess: isSubscriptionSuspended,
        daysRemainingInGracePeriod
    } = usePaymentStatus();

    // Dismissed state is stored in sessionStorage so it resets on the next login session.
    const BANNER_DISMISSED_KEY = "paymentBannerDismissed";
    const [isBannerDismissed, setIsBannerDismissed] = useState<boolean>(
        () => sessionStorage.getItem(BANNER_DISMISSED_KEY) === "true"
    );

    const showPaymentBanner = isPastDueBanner && !isBannerDismissed;

    const handleDismissBanner = () => {
        sessionStorage.setItem(BANNER_DISMISSED_KEY, "true");
        setIsBannerDismissed(true);
    };

    const [hasProfile, setHasProfile] = useState<boolean>(false);
    const [staffDisplayName, setStaffDisplayName] = useState<string>("");

    const termsAccepted = getTermsAccepted() || !!userInfo?.termsAndConditionsAccepted;
    const { avatarUrl: bannerAvatarUrl } = useMyAvatar(
        isAuthenticated && hasProfile && termsAccepted
    );

    // Add ref to track if we're currently refreshing to prevent concurrent refreshes
    const isRefreshingRef = React.useRef(false);

    // Extract userId from current route if viewing a team member
    useEffect(() => {
        const match = location.pathname.match(/\/team\/view\/([^/]+)/);
        const viewedUserId = match ? match[1] : null;
        setCurrentViewedUserId(viewedUserId);
    }, [location.pathname]);

    // Keep hasProfile + staff display name in sync (outlet context for SessionsPage)
    const refreshProfileGate = React.useCallback(async () => {
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;

        const loggedInUserId = getUserIdFromStorage();
        if (!loggedInUserId) {
            isRefreshingRef.current = false;
            return;
        }

        try {
            const url = `${API_BASE_URL}/api/admin-profile`;
            const res = await apiClient(url, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "include"
            });

            if (!res.ok) {
                isRefreshingRef.current = false;
                return;
            }
            const j: any = await res.json();
            const displayName: string =
                [j?.firstName, j?.lastName].filter(Boolean).join(" ") ||
                j?.displayName ||
                "";
            setStaffDisplayName(prev => (prev !== displayName ? displayName : prev));
            setHasProfile(getHasProfile());
        } catch (error) {
            console.error('OuterLayout: Error refreshing profile gate:', error);
        } finally {
            isRefreshingRef.current = false;
        }
    }, []);

    const applyCompanyLogoUrl = React.useCallback((rawUrl?: string | null) => {
        const next = toAbsoluteUrl(rawUrl) ?? null;
        setCompanyLogo(next);
        // Always remount header <img> so a replaced object at the same key reloads
        setCompanyLogoVersion((v) => v + 1);
    }, []);

    // Refresh company logo from profile companyLogoUrl (presigned) — no /api/company/me/logo
    const refreshCompanyLogoBadge = React.useCallback(async () => {
        try {
            if (!isAuthenticated) {
                applyCompanyLogoUrl(null);
                return;
            }

            const userIdToShow = currentViewedUserId || getUserIdFromStorage();

            if (!userIdToShow) {
                applyCompanyLogoUrl(null);
                return;
            }

            const profileUrl = currentViewedUserId
                ? `${API_BASE_URL}/api/admins/${encodeURIComponent(userIdToShow)}/profile`
                : `${API_BASE_URL}/api/admin-profile`;
            const profileRes = await apiClient(profileUrl, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "include",
                cache: "no-store",
            });

            if (!profileRes.ok) {
                console.log('OuterLayout: Profile fetch failed with status:', profileRes.status);
                applyCompanyLogoUrl(null);
                return;
            }

            const profile: any = await profileRes.json();
            applyCompanyLogoUrl(profile?.companyLogoUrl ?? profile?.company_logo_url ?? null);
        } catch (logoError) {
            console.error('OuterLayout: Error fetching company logo:', logoError);
            applyCompanyLogoUrl(null);
        }
    }, [applyCompanyLogoUrl, currentViewedUserId, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            setIsTermsModalOpen(false);
            return;
        }
        const hasAcceptedTerms = getTermsAccepted() || userInfo?.termsAndConditionsAccepted;
        if (hasAcceptedTerms) {
            setIsTermsModalOpen(false);
        } else {
            setIsTermsModalOpen(true);
        }
    }, [isAuthenticated, userInfo?.termsAndConditionsAccepted]);

    useEffect(() => {
        const hasAcceptedTerms = getTermsAccepted();
        if (hasAcceptedTerms) {
            refreshProfileGate();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userInfo?.termsAndConditionsAccepted && isAuthenticated) {
            refreshProfileGate();
            refreshCompanyLogoBadge();
        }
    }, [userInfo?.termsAndConditionsAccepted, isAuthenticated, refreshProfileGate, refreshCompanyLogoBadge]);

    useEffect(() => {
        const hasAcceptedTerms = getTermsAccepted();
        if (hasAcceptedTerms) {
            refreshCompanyLogoBadge();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentViewedUserId]);

    useEffect(() => {
        const handleProfileUpdate = (event: Event) => {
            refreshProfileGate();
            const detail = (event as CustomEvent<{ companyLogoUrl?: string | null }>).detail;
            if (detail && "companyLogoUrl" in detail) {
                applyCompanyLogoUrl(detail.companyLogoUrl ?? null);
            }
            // Refetch profile so we pick up a freshly signed companyLogoUrl after upload
            void refreshCompanyLogoBadge();
        };

        window.addEventListener('profileUpdated', handleProfileUpdate);
        window.addEventListener('profilePictureUpdated', handleProfileUpdate);
        return () => {
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            window.removeEventListener('profilePictureUpdated', handleProfileUpdate);
        };
    }, [applyCompanyLogoUrl, refreshProfileGate, refreshCompanyLogoBadge]);

    useEffect(() => {
        const handleTermsAccepted = () => {
            refreshProfileGate();
            refreshCompanyLogoBadge();
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => window.removeEventListener('termsAccepted', handleTermsAccepted);
    }, [refreshProfileGate, refreshCompanyLogoBadge]);

    const handleCompanyLogoError = () => {
        setCompanyLogo(null);
        setCompanyLogoVersion((v) => v + 1);
    };

    return (
        <div className={`app with-rail-sidebar${showPaymentBanner ? " with-payment-banner" : ""}`}>
            {/* Payment Failure Banner - shown when payment is past due */}
            {showPaymentBanner && (
                <PaymentFailureBanner
                    daysRemaining={daysRemainingInGracePeriod || 0}
                    onClose={handleDismissBanner}
                />
            )}

            <IncompleteAdminProfileProvider>
                <Sidebar
                    companyLogoUrl={companyLogo}
                    companyLogoVersion={companyLogoVersion}
                    profileImageUrl={bannerAvatarUrl}
                    onCompanyLogoError={handleCompanyLogoError}
                />
                <main className="content">
                    <Outlet context={{ staffDisplayName }} />
                </main>
            </IncompleteAdminProfileProvider>

            {/* Terms and Conditions Modal */}
            <TermsAndConditionsModal
                open={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />

            {/* Subscription Suspended Modal - blocks access when suspended */}
            <SubscriptionSuspendedModal isOpen={isSubscriptionSuspended} />
            <TeamChatOverlay />
        </div>
    );
}
